import React from 'react'

import * as c from '../../../common'
import * as r from '../../../react-utils'

import * as Mui from '@mui/material'
import * as MuiIcons from '@mui/icons-material'
import useMediaQuery from '@mui/material/useMediaQuery'
import { push, update } from 'firebase/database'
import { Link, useNavigate } from 'react-router-dom'
import { InstantSearch, SearchBox, useHits } from 'react-instantsearch'

import * as fire from '../../fire'

import CustomerInfo from '../../../app/screens/Customer/CustomerInfo'
import ListCustomer from '../../../app/components/ListCustomer'

import Breadcrumbs from '../../comps/Breadcrumbs'
import Dialog from '../../comps/Dialog'
import EditCustomerButton from '../../comps/EditCustomerButton'
import NavBar from '../../comps/NavBar'
import ToastAlert from '../../comps/ToastAlert'

//@ts-ignore
import styles from './Customers.module.css'

export interface CustomersProps {}

export default React.memo<CustomersProps>(function Customers() {
  const t = r.useTheme()

  const isMounted = r.useIsMounted()
  const navigate = useNavigate()

  //#region addCustomer
  const [addCustomerOpen, setAddCustomerOpen] = React.useState(false)
  const [closerWhenAdding, handleCloserWhenAdding] = React.useState(
    c.CODENAME === 'nirvana' ? 'Zach Johnson' : 'n/a',
  )
  const [customerNameWhenAdding, handleCustomerNameWhenAdding] =
    React.useState('')
  const [customerAddressWhenAdding, handleCustomerAddressWhenAdding] =
    React.useState('')
  const [customerPhoneWhenAdding, handleCustomerPhoneWhenAdding] =
    React.useState('')
  const [customerPhoneAltWhenAdding, handleCustomerPhoneAltWhenAdding] =
    React.useState('')
  const [buildingTypeWhenAdding, handleBuildingTypeWhenAdding] =
    React.useState('')
  const [solarCompanyWhenAdding, handleSolarCompanyWhenAdding] = React.useState(
    c.CODENAME === 'nirvana' ? 'NIRVANA' : '',
  )
  const [productsWhenAdding, handleProductsWhenAdding] = React.useState('')
  const [setterWhenAdding, handleSetterWhenAdding] = React.useState('n/a')
  const [installerWhenAdding, setInstallerWhenAdding] = React.useState(
    c.CODENAME === 'nirvana' ? 'Nirvana Energy' : '',
  )
  const [crmIdWhenAdding, setCrmIDWhenAdding] = React.useState('')

  const [shouldShowWarnings, setShouldShowWarnings] = React.useState(false)
  const [loading, setLoading] = React.useState(false)

  const handleCustomerSubmit = React.useCallback((): void => {
    if (
      !closerWhenAdding ||
      !customerNameWhenAdding ||
      !customerAddressWhenAdding ||
      !customerPhoneWhenAdding ||
      (c.CODENAME === 'nirvana' && !buildingTypeWhenAdding) ||
      !solarCompanyWhenAdding ||
      !setterWhenAdding
    ) {
      setShouldShowWarnings(true)
      return
    }
    setLoading(true)
    ;(async (): Promise<void> => {
      try {
        const normalizedPhone = c.normalizePhoneNumber(customerPhoneWhenAdding)

        if (normalizedPhone.length !== 10) {
          throw new Error(
            'Main phone number should be 10 digits long. E.g. (786) 786 - 7867',
          )
        }

        if (!c.isValidUSPhoneNumber(normalizedPhone)) {
          throw new Error(
            `Invalid main phone number Double-check the area code (${normalizedPhone.slice(
              0,
              3,
            )})`,
          )
        }

        const normalizedPhoneAlt = c.normalizePhoneNumber(
          customerPhoneAltWhenAdding,
        )

        if (normalizedPhoneAlt.length > 0) {
          if (normalizedPhoneAlt.length !== 10) {
            throw new Error(
              'Alternative phone number should be 10 digits long. E.g. (786) 786 - 7867',
            )
          }

          if (!c.isValidUSPhoneNumber(normalizedPhoneAlt)) {
            throw new Error(
              `Invalid alternative phone number.Double-check the area code (${normalizedPhoneAlt.slice(
                0,
                3,
              )})`,
            )
          }
        }

        const customerRef = await push(fire.getCustomersDB(), {})

        if (customerRef.key === null)
          throw new ReferenceError('customerRef.key === null')

        const newCustomer = c.createEmptyCustomer({
          buildingType: buildingTypeWhenAdding,
          crm_id: crmIdWhenAdding,
          customerAddress: customerAddressWhenAdding,
          customerName: customerNameWhenAdding,
          customerPhone: customerPhoneWhenAdding,
          customerPhoneAlt: customerPhoneAltWhenAdding,
          ecohomeRep: closerWhenAdding,
          firebaseKey: customerRef.key,
          main_panel_upgrade_installation_company: installerWhenAdding,
          products: productsWhenAdding,
          solarCompany: solarCompanyWhenAdding,
          solarRep: setterWhenAdding,
        })

        await update(customerRef, newCustomer)

        c.searchIndex.saveObject({
          crm_id: crmIdWhenAdding,
          customerAddress: newCustomer.customerAddress,
          customerName: newCustomer.customerName,
          date: newCustomer.sort_key,
          deleted: false,
          objectID: newCustomer.keyID,
          solarCompany: newCustomer.solarCompany || '',
          solarRep: newCustomer.solarRep || '',
        })

        navigate(customerRef.key)
      } catch (e) {
        console.log(e)
        alert(`Error: ${c.processErr(e)}`)
      } finally {
        setLoading(false)
      }
    })()
  }, [
    closerWhenAdding,
    customerNameWhenAdding,
    customerAddressWhenAdding,
    customerPhoneWhenAdding,
    buildingTypeWhenAdding,
    solarCompanyWhenAdding,
    setterWhenAdding,
    customerPhoneAltWhenAdding,
    crmIdWhenAdding,
    installerWhenAdding,
    productsWhenAdding,
    navigate,
  ])
  //#endregion addCustomer

  const [customerIDBeingDeleted, setCustomerIDBeingDeleted] = React.useState('')
  const [open, setOpen] = React.useState(false)

  const dispatch = c.useDispatch()

  const clearCustomerIDToBeDeleted = React.useCallback(() => {
    setCustomerIDBeingDeleted('')
  }, [])

  const handleCustomerPress = React.useCallback(
    (customerID: string) => {
      navigate(customerID)
    },
    [navigate],
  )

  const undoDeleteCustomer = React.useCallback(() => {
    if (!customerIDBeingDeleted) return
    c.updateCustomer(customerIDBeingDeleted, {
      deleted: false,
    })
    setCustomerIDBeingDeleted('')
  }, [customerIDBeingDeleted])

  const closeResults = React.useCallback(() => {
    if (!isMounted()) {
      return
    }
    setOpen(false)
  }, [isMounted])

  const toggleAddCustomer = React.useCallback(() => {
    setAddCustomerOpen((currentlyOpened) => !currentlyOpened)
  }, [])

  const complexCustomers = c.useSelector(c.selectAllCustomers)

  const customers = React.useMemo(
    (): c.SimpleCustomer[] => complexCustomers.map(c.simplifyCustomer),
    [complexCustomers],
  )

  const authCheckTimeoutRef = React.useRef<NodeJS.Timeout>(
    setTimeout(c.EMPTY_FN, 0),
  )
  React.useEffect(() => {
    if (customers.length > 0) {
      return c.EMPTY_FN
    }
    authCheckTimeoutRef.current = setTimeout(() => {
      if (customers.length === 0 && Boolean(fire.auth.currentUser)) {
        // If no customers have loaded by 10 seconds, probably needs to re-auth
        fire.auth.signOut()
      }
    }, 10000)

    return () => {
      clearTimeout(authCheckTimeoutRef.current)
    }
  }, [customers])

  const onEndReached = React.useCallback((): void => {
    if (customers.length !== c.MAX_CUSTOMERS_AT_ONCE) {
      dispatch(c.requestedMoreCustomers())
    }
  }, [customers.length, dispatch])

  React.useEffect((): c.VoidFn => {
    dispatch(c.subToCustomers())

    return (): void => {
      dispatch(c.unSubFromCustomers())
    }
  }, [dispatch])

  React.useEffect(() => {
    const addMoreCustomers = () => {
      const percent90height = document.body.offsetHeight * 0.95

      if (window.scrollY + window.innerHeight >= percent90height) {
        onEndReached()
      }
    }
    document.addEventListener('scroll', addMoreCustomers)

    return () => {
      document.removeEventListener('scroll', addMoreCustomers)
    }
  }, [onEndReached])

  const handleDragStart = React.useCallback(
    (e: { preventDefault: c.VoidFn }) => {
      e.preventDefault()
    },
    [],
  )
  const handleSearchClick = React.useCallback(() => {
    searchInputRef.current?.focus()
  }, [])
  const searchInputRef = React.useRef<HTMLInputElement>(
    document.createElement('input'),
  )

  const handleClickActiveInput = React.useCallback(() => {
    searchInputRef.current.focus()
  }, [])

  const queryHook = React.useCallback(
    (query: string, search: (value: string) => void) => {
      setOpen(!!query)
      search(query)
    },
    [],
  )

  const searchIconSx = React.useMemo(
    () => ({
      color: t.brand__color,
      ':hover': { cursor: 'text' },
    }),
    [t.brand__color],
  )

  return (
    <>
      <InstantSearch indexName="prod_ecohome" searchClient={c.searchClient}>
        <Mui.Box sx={sx['root']}>
          <Mui.Box sx={sx['padBetweenHeaderAndContent']} />

          {customers.map((c) => (
            <ListCustomer
              customerAddress={c.customerAddress}
              customerName={c.customerName}
              homeRep={c.homeRep}
              itemKey={c.key}
              key={c.key}
              lastModified={c.sort_key}
              onPress={handleCustomerPress}
              solarCompany={c.solarCompany}
              solarRep={c.solarRep}
            />
          ))}

          <Mui.ClickAwayListener onClickAway={closeResults}>
            <Mui.Box sx={open ? sx['boxSearchResults'] : sx['hidden']}>
              <SearchResults />
            </Mui.Box>
          </Mui.ClickAwayListener>
        </Mui.Box>

        <Mui.Stack sx={sx['header']}>
          <NavBar>
            <Search onClick={handleSearchClick} onDragStart={handleDragStart}>
              <MuiIcons.Search
                onClick={handleClickActiveInput}
                sx={searchIconSx}
                width={20}
              />

              <SearchBox
                className={styles['search-box']}
                queryHook={queryHook}
              />
            </Search>
          </NavBar>
        </Mui.Stack>
      </InstantSearch>

      <EditCustomerButton
        bottom={15}
        handleAddCustomerModal={toggleAddCustomer}
        right={15}
      />

      <Breadcrumbs />

      <Dialog
        onClose={toggleAddCustomer}
        opaque
        open={addCustomerOpen}
        title="Add Customer"
      >
        <CustomerInfo
          customerKey=""
          hide={false}
          inputsEditable={true}
          //
          closerWhenAdding={closerWhenAdding}
          onChangeCloserWhenAdding={handleCloserWhenAdding}
          customerNameWhenAdding={customerNameWhenAdding}
          onChangeCustomerNameWhenAdding={handleCustomerNameWhenAdding}
          customerAddressWhenAdding={customerAddressWhenAdding}
          onChangeCustomerAddressWhenAdding={handleCustomerAddressWhenAdding}
          customerPhoneWhenAdding={customerPhoneWhenAdding}
          onChangeCustomerPhoneWhenAdding={handleCustomerPhoneWhenAdding}
          customerPhoneAltWhenAdding={customerPhoneAltWhenAdding}
          onChangeCustomerPhoneAltWhenAdding={handleCustomerPhoneAltWhenAdding}
          buildingTypeWhenAdding={buildingTypeWhenAdding}
          onChangeBuildingTypeWhenAdding={handleBuildingTypeWhenAdding}
          solarCompanyWhenAdding={solarCompanyWhenAdding}
          onChangeSolarCompanyWhenAdding={handleSolarCompanyWhenAdding}
          productsWhenAdding={productsWhenAdding}
          onChangeProductsWhenAdding={handleProductsWhenAdding}
          setterWhenAdding={setterWhenAdding}
          installerWhenAdding={installerWhenAdding}
          onChangeInstallerWhenAdding={setInstallerWhenAdding}
          onChangeSetterWhenAdding={handleSetterWhenAdding}
          crmIDWhenAdding={crmIdWhenAdding}
          onChangeCrmIDWhenAdding={setCrmIDWhenAdding}
          loading={loading}
          onSaveWhenAdding={handleCustomerSubmit}
          shouldShowWarnings={shouldShowWarnings}
        />
      </Dialog>

      <ToastAlert
        duration={4000}
        handleClose={clearCustomerIDToBeDeleted}
        message="Customer Deleted"
        onClick={undoDeleteCustomer}
        open={customerIDBeingDeleted ? true : false}
        severity="info"
        stopTime={customerIDBeingDeleted ? false : true}
        type="undoButton"
      />
    </>
  )
})

const SearchResults = () => {
  const { items: hits } = useHits<c.Customer>()
  const isDark = useMediaQuery('(prefers-color-scheme: dark)')
  // Algolia re-renders unnecessarily thus necessitating this useMemo() here
  const sanitized = React.useMemo(() => {
    const validated = hits.map(c.validateCustomer).filter((c) => c.ok)

    return (
      validated
        .map((r) => r.sanitized)
        // A typical ID is 20 characters long
        .filter((c) => c.key.length >= 18)
        .filter((c) => !c.deleted)
    )
  }, [hits])

  React.useEffect(() => {
    c.dispatch(
      c.receivedSimpleCustomers({
        customers: sanitized.map(c.simplifyCustomer),
      }),
    )
  }, [sanitized])

  return (
    <Mui.Box
      component={Mui.Paper}
      elevation={9}
      sx={
        sanitized.length >= 9
          ? isDark
            ? sx['boxResultsDark']
            : sx['boxResults']
          : isDark
          ? sx['boxResultsAutoHeightDark']
          : sx['boxResultsAutoHeight']
      }
    >
      {sanitized.map((customer) => (
        <Link
          key={customer.key}
          style={!customer.deleted ? linkStyle : hiddenStyle}
          to={`/customers/${customer.objectID}`}
        >
          <Mui.Box
            component={Mui.Paper}
            elevation={6}
            sx={isDark ? sx['resultDark'] : sx['result']}
          >
            <Mui.Box sx={sx['boxResultsItemText']}>
              <Mui.Typography
                color={isDark ? c.white : c.black}
                sx={sx['typographyResultsItemText']}
              >
                {customer.customerName}
              </Mui.Typography>
            </Mui.Box>

            <Mui.Box sx={sx['textRow']}>
              <Mui.Typography
                color={isDark ? c.white : c.black}
                sx={sx['typographyResultsItemText']}
              >
                {customer.customerAddress}
              </Mui.Typography>
            </Mui.Box>
            <Mui.Box sx={sx['boxSolarRepText']}>
              <Mui.Typography
                color={isDark ? c.white : c.black}
                sx={sx['typographyResultsItemText']}
              >
                {customer.solarRep}
              </Mui.Typography>
            </Mui.Box>
          </Mui.Box>
        </Link>
      ))}
    </Mui.Box>
  )
}

const boxResultsBase = {
  borderRadius: '10px',
  display: 'flex',
  flexDirection: 'column',
  left: '50%',
  overflowY: 'auto',
  padding: '5px',
  position: 'fixed',
  top: '96px',
  transform: 'translate(-50%, 0%)',
} as const

const resultBase = {
  alignItems: 'center',
  display: 'flex',
  fontFamily: c.light.fontFamily,
  height: '50px',
  justifyContent: 'space-around',
  marginBottom: '5px',
  width: '100%',
}

export const rootSurfaceCustomers: c.Surface = 'canvas'
const sx = {
  boxIcon: {
    backgroundColor: '#3b8ad9',
    borderRadius: '5px 0 0px 5px',
    display: 'flex',
    height: '100%',
    justifyContent: 'center',
    position: 'absolute',
    width: '30px',
  },
  boxIconDark: {
    backgroundColor: '#474747',
    borderRadius: '5px 0 0px 5px',
    display: 'flex',
    height: '100%',
    justifyContent: 'center',
    position: 'absolute',
    width: '30px',
  },
  boxInputSearch: {
    alignSelf: 'center',
    display: 'flex',
    height: '100%',
    width: '80%',
  },
  root: {
    display: 'flex' as const,
    gap: '16px',
    flexDirection: 'column' as const,
    maxWidth: '720px',
    margin: 'auto',
    paddingBottom: '128px',
  },
  boxSearchResults: { display: 'flex' },
  boxResultsAutoHeight: {
    ...boxResultsBase,
    backgroundColor: c.light.canvas.backgroundColor as string,
    height: 'auto',
  },
  boxResultsAutoHeightDark: {
    ...boxResultsBase,
    backgroundColor: c.dark.canvas.backgroundColor as string,
    height: 'auto',
  },
  boxResults: {
    ...boxResultsBase,
    backgroundColor: c.light.canvas.backgroundColor as string,
    height: '480px',
  },
  boxResultsDark: {
    ...boxResultsBase,
    backgroundColor: c.dark.canvas.backgroundColor as string,
    height: '480px',
  },
  boxResultsItemText: {
    alignItems: 'center',
    display: 'flex',
    height: '100%',
    justifyContent: 'center',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  padBetweenHeaderAndContent: {
    display: 'flex',
    height: '88px',
    width: '100%',
  },
  boxSolarRepText: {
    alignItems: 'center',
    display: { xs: 'none', md: 'flex' },
    height: '100%',
    justifyContent: 'center',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  hidden: { display: 'none' },
  noZ: { zIndex: 'inherit', backgroundColor: 'transparent' },
  header: {
    height: '70px',
    position: 'fixed',
    // Breadcrumb height
    paddingTop: '28px',
    top: 0,
    width: '100%',
  },
  result: {
    ...resultBase,
    backgroundColor: c.light.canvas.backgroundColor as string,
  },
  resultDark: {
    ...resultBase,
    backgroundColor: c.dark.canvas.backgroundColor as string,
  },
  table: { minWidth: 600 },
  tableDark: {
    backgroundColor: c.dark.canvas.backgroundColor as string,
    minWidth: 600,
  },
  textRow: {
    alignItems: 'center',
    display: 'flex',
    height: '100%',
    justifyContent: 'center',
  },
  typographyResultsItemText: {
    overflow: 'hidden',
    textAlign: 'center',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: { sm: '180px', xs: '150px' },
  },
  tableContainer: { display: { md: 'block', xs: 'none' } },
  tableRow: { display: 'table-row' },
}

const linkStyle = {
  textDecoration: 'none',
}

const hiddenStyle = {
  display: 'none',
}

const Search = Mui.styled('div')(({ theme }) => ({
  alignItems: 'center',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: Mui.alpha(theme.palette.common.white, 0.15),
  display: 'flex',
  flexDirection: 'row',
  marginRight: theme.spacing(2),
  marginLeft: 0,
  paddingLeft: 16,
  [theme.breakpoints.up('sm')]: {
    marginLeft: theme.spacing(3),
    width: 'auto',
  },
  ':hover': {
    cursor: 'text',
  },
}))
